import React, { useCallback, useEffect, useRef, useState } from "react";
import "./Player.css";
import { useDispatch, useSelector } from "react-redux";
import { selectApp, setCurrentWatch, setLoading } from "./app/appSlice";
import { useLocation } from "react-router-dom";
import BottomBar from "./components/Watch/BottomBar";
import ProgressBar from "./components/Watch/ProgressBar";
import { gsap } from "gsap";
import SpatialNavigation from "spatial-navigation-js";

const PlayerScreen = ({ playerRef }) => {
  const {
    selectedEpisode,
    isPlaying,
    data,
    isPlayerReady,
    isOverlay,
    isEpisodesBarFocused,
    isProgressBarFocused,
    isSeeking,
    showResumePrompt,
    seekTime
  } = useSelector(selectApp).currentWatch;
  const [isAPIReady, setIsAPIReady] = useState(false);
  const [videoInCue, setVideoInCue] = useState(false);
  const [isVideoEndState, setIsVideoEndState] = useState(false);

  const dispatch = useDispatch();
  const location = useLocation();
  const hideTimerRef = useRef(null);

  const videoWrapperRef = useRef();

  useEffect(() => {
    if (videoWrapperRef.current) {
      if (isPlayerReady && videoInCue) {
        gsap.set(videoWrapperRef.current, {
          opacity: 0,
          autoAlpha: 0,
          pointerEvents: "none",
        });

        gsap.to(videoWrapperRef.current, {
          autoAlpha: 1, // opacity + visibility: visible
          duration: 0.5,
          ease: "power2.in",
          pointerEvents: "auto",
        });
      } else {
        gsap.to(videoWrapperRef.current, {
          autoAlpha: 0, // opacity + visibility: hidden
          duration: 0.5,
          ease: "power2.out",
          pointerEvents: "none",
        });
      }
    }
  }, [isPlayerReady, videoInCue]);

  useEffect(() => {
    const onYouTubeIframeAPIReady = () => {
      setIsAPIReady(true);
    };

    if (window.YT) {
      setIsAPIReady(true);
    } else {
      window.onYouTubeIframeAPIReady = onYouTubeIframeAPIReady;
    }
  }, []);

  useEffect(() => {
    if (location.pathname.includes("watch")) {
      if (playerRef.current && selectedEpisode.video_id) {
        const resume = localStorage.getItem(selectedEpisode.video_id);
        playerRef.current.cueVideoById({
          videoId: selectedEpisode.video_id,
          startSeconds: resume,
        });
        setVideoInCue(true);
      }
    }
  }, [location, selectedEpisode, playerRef]);

  // Handle video end state and auto-play the next episode
  useEffect(() => {
    if (isVideoEndState) {
      localStorage.removeItem(selectedEpisode.video_id);

      const currentWatchId = selectedEpisode.episode_id;
      const nextWatchIndex =
        data.episodes?.findIndex(
          (episode) => parseInt(episode.episode_id) === parseInt(currentWatchId)
        ) + 1;

      if (nextWatchIndex !== -1 && data.episodes.length > nextWatchIndex) {
        const nextEpisode = data.episodes[nextWatchIndex];
        dispatch(
          setCurrentWatch({ selectedEpisode: nextEpisode, isPlaying: false })
        );
      }

      setIsVideoEndState(false);
    }
  }, [selectedEpisode, data, isVideoEndState, dispatch]);

  // Overlay timer
  const resetOverlayTimer = useCallback(() => {
    dispatch(setCurrentWatch({ isOverlay: true }));
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);

    hideTimerRef.current = setTimeout(() => {
      dispatch(
        setCurrentWatch({
          isOverlay: false,
          isEpisodesBarFocused: false,
          isProgressBarFocused: true,
        })
      );
    }, 10000);
  }, [dispatch]);

  useEffect(() => {
    if (isAPIReady && !playerRef.current) {
      playerRef.current = new window.YT.Player("player-screen", {
        videoId: "",
        playerVars: {
          autoplay: 0,
          controls: 0,
          mute: 0,
          modestbranding: 1, // Reduce YouTube branding
          disablekb: 1, // Disable keyboard controls
          showinfo: 0, // Hide video information
          iv_load_policy: 3, // Hide video annotations
          rel: 0, // Prevent related videos from showing
          ecver: 2,
        },
        events: {
          onReady: (event) => {
            console.log("YouTube Player is ready");
          },
          onStateChange: (event) => {
            if (
              event.data === window.YT.PlayerState.CUED &&
              window.location.href.includes("watch")
            ) {
              const currentTime = playerRef.current.getCurrentTime();
              if (currentTime > 10) {
                dispatch(setCurrentWatch({showResumePrompt: true }));
              } else {
                playerRef.current.playVideo();
              }
              dispatch(
                setCurrentWatch({ isPlaying: true, isPlayerReady: true })
              );
            } else if (event.data === window.YT.PlayerState.ENDED) {
              setIsVideoEndState(true);
            } else if (event.data === window.YT.PlayerState.PLAYING) {
              dispatch(setCurrentWatch({ isPlaying: true }));
            }
          },
        },
      });
    }
  }, [
    dispatch,
    playerRef,
    isAPIReady,
    selectedEpisode.video_id,
    isVideoEndState,
  ]);

  useEffect(() => {
    if (playerRef.current && playerRef.current.stopVideo) {
      if (!location.pathname.includes("watch")) {
        dispatch(setLoading(true));
        playerRef.current.stopVideo();
        playerRef.current.clearVideo();
        dispatch(setCurrentWatch({ isPlaying: false, isPlayerReady: false, showResumePrompt: false, isOverlay: false }));
        setVideoInCue(false);
      }
    }
  }, [dispatch, location, playerRef, selectedEpisode.video_id]);

  const handlePlayPause = useCallback(() => {
    const player = playerRef.current;
    if (player && player.playVideo && player.pauseVideo) {
      if (isPlaying) {
        player.pauseVideo();
      } else {
        player.playVideo();
      }
      dispatch(setCurrentWatch({ isPlaying: !isPlaying }));
    }
  }, [isPlaying, dispatch, playerRef]);

  useEffect(() => {
    SpatialNavigation.remove("watch-resume");
    if (showResumePrompt) {
      SpatialNavigation.add({
        id: "watch-resume",
        selector: "#watch-resume .focusable",
        defaultElement: "#watch-resume .focusable:first-child",
        enterTo: "last-focused",
        rememberSource: true,
      });

      SpatialNavigation.makeFocusable();
      
      setTimeout(() => {
        SpatialNavigation.focus("watch-resume");
      },500)
    } else {
      SpatialNavigation.remove("watch-resume");
    }
  }, [showResumePrompt]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const player = playerRef.current;
      if (!player || !isPlayerReady || showResumePrompt) return;

      // Avoid action if Alt + ArrowLeft is pressed
      if (e.altKey && (e.key === "ArrowLeft" || e.keyCode === 37)) return;

      // Bottombar is not loaded
      if (
        typeof document
          .getElementById("watch-sidebar")
          .getElementsByClassName("focusable")[0] === "undefined"
      )
        return;

      resetOverlayTimer();

      // Prevent default scroll behavior when focused on ProgressBar
      if (isProgressBarFocused && (["ArrowLeft", "ArrowRight"].includes(e.key) || [37, 39].includes(e.keyCode)) ) {
        e.preventDefault();
        return;
      }

      if (
        (e.key === "Enter" || e.keyCode === 13) &&
        !isEpisodesBarFocused &&
        !isSeeking
      ) {
        handlePlayPause();
      } else if (e.key === "ArrowDown" || e.keyCode === 40) {
        if(isOverlay && isEpisodesBarFocused) {
          dispatch(setCurrentWatch({isOverlay: false, isProgressBarFocused: true, isEpisodesBarFocused: false}));
        }
        if(isOverlay && isProgressBarFocused) {
          dispatch(setCurrentWatch({isOverlay: true, isProgressBarFocused: false, isEpisodesBarFocused: true}));
        }
        if(!isOverlay) {
          dispatch(
            setCurrentWatch({
              isEpisodesBarFocused: true,
              isProgressBarFocused: false,
              isOverlay: true
            })
          );
        }
      } else if (e.key === "ArrowUp" || e.keyCode === 38) {
        dispatch(
          setCurrentWatch({
            isEpisodesBarFocused: false,
            isProgressBarFocused: true,
          })
        );
        document.activeElement.blur();

        if (isProgressBarFocused && isPlaying && isOverlay && !isSeeking) {
          dispatch(setCurrentWatch({ isOverlay: false }));
        } else {
          dispatch(setCurrentWatch({ isOverlay: true }));
        }
      } else if (
        ((e.key === "ArrowLeft" || e.keyCode === 37) || (e.key === "ArrowRight" || e.keyCode === 39)) &&
        !isEpisodesBarFocused
      ) {
        dispatch(
          setCurrentWatch({
            isEpisodesBarFocused: false,
            isProgressBarFocused: true,
          })
        );
      }
    };

    // Add the listener only when the path includes "/watch"
    if (location.pathname.includes("/watch") && isPlayerReady) {
      window.addEventListener("keydown", handleKeyDown);
    } else {
      window.removeEventListener("keydown", handleKeyDown);
      dispatch(
        setCurrentWatch({
          isEpisodesBarFocused: false,
          isProgressBarFocused: true,
        })
      );
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    dispatch,
    handlePlayPause,
    isEpisodesBarFocused,
    isProgressBarFocused,
    isPlayerReady,
    playerRef,
    location.pathname,
    resetOverlayTimer,
    isOverlay,
    isPlaying,
    showResumePrompt,
    isSeeking,
  ]);

  const formatResumeTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    // For durations with hours (never shows seconds)
    if (hours > 0) {
      return `${hours} hour${hours !== 1 ? "s" : ""}${
        minutes > 0 ? ` ${minutes} minute${minutes !== 1 ? "s" : ""}` : ""
      }`.trim();
    }

    // For minute-only durations (never shows "0 minutes")
    if (minutes > 0) {
      return `${minutes} minute${minutes !== 1 ? "s" : ""}${
        seconds > 0 ? ` ${seconds} second${seconds !== 1 ? "s" : ""}` : ""
      }`.trim();
    }

    // Fallback for pure seconds (<1 minute)
    return `${seconds} second${seconds !== 1 ? "s" : ""}`;
  };

  return (
    <>
      {/* {<h1>{isOverlay.toString()} - {isEpisodesBarFocused.toString()} - {isProgressBarFocused.toString()}</h1>} */}

      <div
        ref={videoWrapperRef}
        className={`videoWrapper ${
          isPlayerReady && videoInCue ? `d-block` : "d-none"
        }`}
      >
        <div className={`fullscreen no-pointer-events ${showResumePrompt ? 'opacity-h' : 'opacity-f'}`}>
          <div id="player-screen" className={`fullscreen no-pointer-events`} />
        </div>

        {showResumePrompt && (
          <div id="watch-resume" className="paused-overlay" style={{backgroundColor: 'var(--dark)'}}>
            <div className="">
              <p>
                Resume from{" "}
                {formatResumeTime(playerRef.current.getCurrentTime())} ?
              </p>
              <center>
                <button
                  className="btn btn-light focusable"
                  style={{ marginRight: 14 }}
                  onClick={() => {
                    playerRef.current.playVideo();
                    setTimeout(() => {
                      dispatch(setCurrentWatch({ isPlaying: true, showResumePrompt: false }));
                    },500);
                  }}
                >
                  Yes
                </button>
                <button
                  className="btn btn-light focusable"
                  onClick={() => {
                    playerRef.current.seekTo(0);
                    playerRef.current.playVideo();
                    setTimeout(() => {
                      dispatch(setCurrentWatch({ isPlaying: true, showResumePrompt: false }));
                    },500);
                  }}
                >
                  No
                </button>
              </center>
            </div>
          </div>
        )}

        {!isPlaying && !isSeeking && !isPlayerReady && (
          <div className="paused-overlay">Paused</div>
        )}
 
        {isSeeking && !showResumePrompt && seekTime !== '00:00' &&
          <div className="paused-overlay">{seekTime}</div>
        }

        {(
          <div
            id="overlay"
            style={{visibility: showResumePrompt ? 'hidden' : 'visible'}}
            className={`${ (isOverlay || !isPlaying || isSeeking)  ? "d-block" : "d-none"} overlay-container`}
          >
            <div className="progress-bar-overlay">
              <ProgressBar
                playerRef={playerRef}
                isPlayerReady={isPlayerReady}
                isVideoEndState={isVideoEndState}
              />
            </div>

            <div id="watch-sidebar" className="bottom-bar-overlay">
              <BottomBar selectedEpisode={selectedEpisode} />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PlayerScreen;
